import { HighlightCard } from '@/components';
import TextTooltip from '@/components/tooltips/TextTooltip';
import { usePayoutEarningsQuery } from '@/services/revenueApi';
import { SelectedNetwork } from '@/types';
import { formatCurrency } from '@/utils/currencyUtils';
import { formatYearMonth } from '@/utils/dateUtils/dateFormatUtils';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { useRef, useState } from 'react';

type Props = {
  selectedNetwork: SelectedNetwork;
  year: string;
  month: string;
};

export const Highlights = ({ selectedNetwork, year, month }: Props) => {
  const { isPending, data } = usePayoutEarningsQuery(selectedNetwork.id, year, month);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverRefTotal = useRef<HTMLDivElement>(null);
  const [cooldown, setCooldown] = useState(false);

  const handleMouseOver = () => {
    if (!cooldown) {
      mixpanelTrack(MIXPANEL_EVENT.payouts.highlights.includeEarningsTooltipHover, {
        widget: RevenueDashboardWidget.PayoutMonthHighlights,
        page: 'earnings',
      });

      // We don't want to spam events so a 5 second cooldown here to prevent multiple events from being triggered
      setCooldown(true);
      setTimeout(() => {
        setCooldown(false);
      }, 5000);
    }
  };

  return (
    <div className="col-span-12 flex flex-col lg:flex-row gap-2 xl:gap-6 w-full">
      <HighlightCard
        className="pt-0 lg:flex-basis-auto lg:min-w-[50%] lg:max-w-[66.66667%]"
        title={
          <span className="inline-flex items-center gap-1">
            Includes some of the earnings from{' '}
            <TextTooltip
              onMouseOver={handleMouseOver}
              popoverRef={popoverRef}
              description="Only includes earnings from campaigns that were ready for payout, not all earnings from these months."
            />
          </span>
        }
        value={
          <ul className="inline-flex gap-2 flex-wrap">
            {data?.calculation_period?.map((dateStr) => (
              <li key={dateStr} className="bg-[#EDEDED] rounded-md py-1 px-2 font-[500]">
                {formatYearMonth(dateStr)}
              </li>
            ))}
          </ul>
        }
        loading={isPending}
      />
      <HighlightCard
        className="pt-0 flex-1"
        title={
           <span className="inline-flex items-center gap-1">
            Total revenue
            <TextTooltip
              popoverRef={popoverRefTotal}
              description="Figures do not include VAT / fees etc. download PDFs for full details."
            />
          </span>
        }
        value={formatCurrency(data?.total?.amount ?? 0, data?.total?.currency).toString()}
        loading={isPending}
      />
    </div>
  );
};
